import { uiText } from '@/i18n/interface';

import { useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';
import { toast } from "@/hooks/use-toast";

interface ParticipationStat {
  id: string;
  student_id: string;
  session_id: string;
  attended: boolean;
  session_date: string;
  session_title: string;
  age_group: 'all' | 'kids' | 'teens' | 'adults';
  created_at: string;
  student: {
    first_name: string;
    last_name: string;
  };
}

interface StudentStats {
  student_id: string;
  student_name: string;
  total_sessions: number;
  attended_sessions: number;
  attendance_rate: number;
  last_attendance: string | null;
}

const formatDateToDDMMYYYY = (dateString: string) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

export const useParticipationStats = () => {
  const [participationStats, setParticipationStats] = useState<ParticipationStat[]>([]);
  const [studentStats, setStudentStats] = useState<StudentStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const closePastSessions = async () => {
    try {
      const { error } = await supabase.rpc('close_past_sessions');
      if (error) throw error;

      toast({
        title: uiText("Sessions Updated"),
        description: uiText("Past training sessions have been closed and participation stats updated.")
      });

      // Refresh the data after closing sessions
      await fetchParticipationStats();
    } catch (error) {
      console.error('Error closing past sessions:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to close past sessions."),
        variant: "destructive"
      });
    }
  };

  const fetchParticipationStats = async () => {
    try {
      setIsLoading(true);

      // Fetch participation stats with student names
      const { data: statsData, error: statsError } = await supabase
        .from('participation_stats')
        .select(`
          *,
          student:profiles!participation_stats_student_id_fkey(
            first_name,
            last_name
          )
        `)
        .order('session_date', { ascending: false });

      if (statsError) throw statsError;

      // Cast age_group to the expected type
      const typedStatsData = statsData?.map(stat => ({
        ...stat,
        age_group: (stat.age_group as 'all' | 'kids' | 'teens' | 'adults') || 'all'
      })) || [];

      setParticipationStats(typedStatsData);

      // Calculate aggregated student statistics
      const studentStatsMap: { [key: string]: StudentStats } = {};

      typedStatsData?.forEach((stat) => {
        const studentId = stat.student_id;
        const studentName = `${stat.student.first_name} ${stat.student.last_name}`;

        if (!studentStatsMap[studentId]) {
          studentStatsMap[studentId] = {
            student_id: studentId,
            student_name: studentName,
            total_sessions: 0,
            attended_sessions: 0,
            attendance_rate: 0,
            last_attendance: null
          };
        }

        studentStatsMap[studentId].total_sessions++;
        if (stat.attended) {
          studentStatsMap[studentId].attended_sessions++;
          if (!studentStatsMap[studentId].last_attendance ||
              stat.session_date > studentStatsMap[studentId].last_attendance!) {
            studentStatsMap[studentId].last_attendance = stat.session_date;
          }
        }
      });

      // Calculate attendance rates
      const studentStatsArray = Object.values(studentStatsMap).map(stats => ({
        ...stats,
        attendance_rate: stats.total_sessions > 0
          ? Math.round((stats.attended_sessions / stats.total_sessions) * 100)
          : 0
      }));

      setStudentStats(studentStatsArray.sort((a, b) => b.attendance_rate - a.attendance_rate));

    } catch (error) {
      console.error('Error fetching participation stats:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to load participation statistics."),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchParticipationStats();
  }, []);

  return {
    participationStats,
    studentStats,
    isLoading,
    closePastSessions,
    refetchStats: fetchParticipationStats
  };
};
